import { Input, Button } from '@/lib';
import { SpinnerIcon } from '@/lib/Spinner';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { LuCheck, LuLandmark } from 'react-icons/lu';
import { citizenTraits, hasSubmittedCitizenForm, identifyCitizen, isValidFiscalCode, markCitizenFormSubmitted, type PublicCitizenForm } from './registration';
import './citizen-form.css';

export const PublicCitizenFormPage = () => {
  const { datasourceId } = useParams<{ datasourceId: string }>();
  const { t } = useTranslation('comuni');
  const [configuration, setConfiguration] = useState<PublicCitizenForm>();
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const [submitted, setSubmitted] = useState(() => hasSubmittedCitizenForm(datasourceId));
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [error, setError] = useState(false);
  const [consent, setConsent] = useState(false);
  const [channels, setChannels] = useState({ whatsapp: true, sms: true, email: false });

  useEffect(() => {
    const controller = new AbortController();
    setConfiguration(undefined);
    setLoadError(false);
    setSubmitted(hasSubmittedCitizenForm(datasourceId));
    fetch(`${(import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')}/v1/public/citizen-forms/${encodeURIComponent(datasourceId)}`, {
      signal: controller.signal,
      credentials: 'omit',
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Form unavailable');
        const data: PublicCitizenForm = await response.json();
        if (!data.municipalityName || !data.privacyUrl || !data.scriptUrl || !data.ingestionUrl || data.datasourceId !== datasourceId)
          throw new Error('Invalid form');
        if (!controller.signal.aborted) setConfiguration(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadError(true);
      });
    return () => controller.abort();
  }, [datasourceId, reload]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sendingRef.current || !configuration || !consent || !Object.values(channels).some(Boolean)) return;
    if (hasSubmittedCitizenForm(datasourceId)) {
      setSubmitted(true);
      return;
    }
    const fiscalCode = event.currentTarget.elements.namedItem('fiscal_code') as HTMLInputElement;
    fiscalCode.setCustomValidity(isValidFiscalCode(fiscalCode.value) ? '' : t('publicForm.fiscalCodeInvalid'));
    if (!event.currentTarget.reportValidity()) return;
    const traits = citizenTraits(new FormData(event.currentTarget), configuration);
    sendingRef.current = true;
    setSending(true);
    setError(false);
    try {
      await identifyCitizen(configuration, traits);
      markCitizenFormSubmitted(datasourceId);
      setJustSubmitted(true);
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  if (!configuration)
    return (
      <main className='citizen-form-page flex min-h-screen items-center justify-center p-6'>
        {loadError ? (
          <div className='max-w-md text-center' role='alert'>
            <h1 className='text-2xl font-bold text-primary-900'>{t('publicForm.unavailable')}</h1>
            <p className='my-4 text-tertiary-600'>{t('publicForm.unavailableDescription')}</p>
            <Button intent='primary' size='lg' onClick={() => setReload((value) => value + 1)}>
              {t('lists.retry')}
            </Button>
          </div>
        ) : (
          <div role='status' aria-label={t('publicForm.loading')}>
            <SpinnerIcon className='size-8 text-brand-700' />
          </div>
        )}
      </main>
    );

  const fieldClass = 'h-14 rounded-xl border-2 border-slate-300 bg-white px-4 text-lg text-slate-900 shadow-none placeholder:text-slate-500';

  return (
    <main className='citizen-form-page min-h-screen sm:px-6 sm:py-10'>
      <article className='mx-auto w-full max-w-xl overflow-hidden bg-slate-50 sm:rounded-2xl'>
        <header className='flex items-center gap-4 bg-[#003da6] px-6 py-6 text-white sm:px-8'>
          <span className='flex size-14 shrink-0 items-center justify-center rounded-xl bg-white text-[#003da6]' aria-hidden='true'>
            <LuLandmark className='size-7' />
          </span>
          <h1 className='min-w-0 text-xl font-bold leading-tight sm:text-2xl'>
            <span className='mb-1 block text-sm font-semibold tracking-wide text-blue-100'>{t('publicForm.municipality')}</span>
            {configuration.municipalityName}
          </h1>
        </header>
        {submitted ? (
          <section className='px-6 py-12 text-center sm:px-8' role='status'>
            <LuCheck className='mx-auto mb-5 size-12 text-[#003da6]' aria-hidden='true' />
            <h2 className='text-2xl font-bold text-slate-900'>{t(justSubmitted ? 'publicForm.success' : 'publicForm.alreadySubmitted')}</h2>
            <p className='mt-4 text-base leading-relaxed text-slate-600'>
              {t(justSubmitted ? 'publicForm.successDescription' : 'publicForm.alreadySubmittedDescription', { municipality: configuration.municipalityName })}
            </p>
          </section>
        ) : (
          <form onSubmit={submit}>
            <fieldset disabled={sending} className='min-w-0 px-6 pb-8 pt-7 sm:px-8'>
              <h2 className='text-[28px] font-extrabold leading-tight tracking-tight text-slate-900'>{t('publicForm.title')}</h2>
              <p className='mb-8 mt-4 text-lg leading-relaxed text-slate-600'>{t('publicForm.description')}</p>
              <div className='grid gap-6'>
                <div>
                  <label htmlFor='citizen-name' className='mb-2 block text-base font-bold text-slate-900'>
                    {t('publicForm.name')} <span className='text-red-600'>*</span>
                  </label>
                  <Input
                    id='citizen-name'
                    name='name'
                    required
                    maxLength={160}
                    pattern='.*\S.*'
                    autoComplete='name'
                    placeholder='Mario Rossi'
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor='citizen-fiscal-code' className='mb-2 block text-base font-bold text-slate-900'>
                    {t('publicForm.fiscalCode')} <span className='text-red-600'>*</span>
                  </label>
                  <Input
                    id='citizen-fiscal-code'
                    name='fiscal_code'
                    onInput={(event) => event.currentTarget.setCustomValidity('')}
                    required
                    pattern='[A-Za-z0-9]{16}'
                    minLength={16}
                    maxLength={16}
                    autoCapitalize='characters'
                    spellCheck={false}
                    aria-describedby='fiscal-code-help'
                    placeholder='RSSMRA80A01H501U'
                    className={`${fieldClass} uppercase`}
                  />
                  <p id='fiscal-code-help' className='mt-2 text-sm leading-relaxed text-slate-600'>
                    {t('publicForm.fiscalCodeHelp')}
                  </p>
                </div>
                <div>
                  <label htmlFor='citizen-address' className='mb-2 block text-base font-bold text-slate-900'>
                    {t('publicForm.address')} <span className='text-red-600'>*</span>
                  </label>
                  <Input
                    id='citizen-address'
                    name='address_street'
                    required
                    maxLength={300}
                    pattern='.*\S.*'
                    autoComplete='street-address'
                    placeholder={t('publicForm.addressPlaceholder', { municipality: configuration.municipalityName })}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor='citizen-phone' className='mb-2 block text-base font-bold text-slate-900'>
                    {t('publicForm.phone')} <span className='text-red-600'>*</span>
                  </label>
                  <div className='flex gap-3'>
                    <span className='flex h-14 items-center rounded-xl border-2 border-slate-300 px-4 text-lg text-slate-600' aria-hidden='true'>
                      +39
                    </span>
                    <Input
                      id='citizen-phone'
                      name='phone'
                      type='tel'
                      inputMode='tel'
                      autoComplete='tel-national'
                      required
                      pattern='3(?: *[0-9]){8,9} *'
                      maxLength={16}
                      aria-describedby='phone-help'
                      placeholder='333 123 4567'
                      className={fieldClass}
                    />
                  </div>
                  <p id='phone-help' className='mt-2 text-sm text-slate-600'>
                    {t('publicForm.phoneHelp')}
                  </p>
                </div>
                <div>
                  <label htmlFor='citizen-email' className='mb-2 block text-base font-bold text-slate-900'>
                    {t('publicForm.email')} {!channels.email && <span className='font-normal text-slate-600'>{t('publicForm.optional')}</span>}
                    {channels.email && <span className='text-red-600'> *</span>}
                  </label>
                  <Input
                    id='citizen-email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    required={channels.email}
                    maxLength={254}
                    placeholder='mario.rossi@email.it'
                    className={fieldClass}
                  />
                </div>
              </div>
              <fieldset className='mt-8'>
                <legend className='mb-3 text-base font-bold text-slate-900'>{t('publicForm.channels')}</legend>
                <div className='grid gap-3'>
                  {(['whatsapp', 'sms', 'email'] as const).map((channel) => (
                    <label
                      key={channel}
                      className={`flex cursor-pointer items-center gap-4 rounded-xl border-2 p-4 ${channels[channel] ? 'border-[#003da6] bg-blue-50' : 'border-slate-300 bg-white'}`}
                    >
                      <input
                        type='checkbox'
                        name={channel === 'email' ? 'email_channel' : channel}
                        checked={channels[channel]}
                        onChange={(event) => setChannels((current) => ({ ...current, [channel]: event.target.checked }))}
                        className='size-6 shrink-0 accent-[#003da6]'
                      />
                      <span>
                        <span className='block text-lg font-bold text-slate-900'>{t(`publicForm.channel.${channel}`)}</span>
                        <span className='mt-1 block text-sm leading-relaxed text-slate-600'>{t(`publicForm.channelHelp.${channel}`)}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
              {!Object.values(channels).some(Boolean) && (
                <p className='mt-3 text-sm text-red-700' role='alert'>
                  {t('publicForm.selectChannel')}
                </p>
              )}
              <div className='mt-7 flex items-start gap-4'>
                <input
                  id='citizen-consent'
                  type='checkbox'
                  name='consent'
                  required
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                  className='mt-1 size-6 shrink-0 accent-[#003da6]'
                />
                <label htmlFor='citizen-consent' className='text-base leading-relaxed text-slate-700'>
                  {t('publicForm.consentBefore')}
                  <a
                    href={configuration.privacyUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='font-medium text-[#003da6] underline underline-offset-4'
                  >
                    {t('publicForm.privacy')}
                  </a>{' '}
                  {t('publicForm.consentAfter', { municipality: configuration.municipalityName })} <span className='text-red-600'>*</span>
                </label>
              </div>
              {error && (
                <p role='alert' className='mt-5 text-base text-red-700'>
                  {t('publicForm.sendError')}
                </p>
              )}
            </fieldset>
            <div className='border-t border-slate-200 bg-white px-6 pb-5 pt-5 sm:px-8'>
              <Button
                type='submit'
                size='xl'
                intent='primary'
                disabled={!consent || !Object.values(channels).some(Boolean)}
                isLoading={sending}
                className='w-full rounded-xl bg-[#003da6] py-4 text-lg font-bold'
              >
                {t(sending ? 'publicForm.sending' : 'publicForm.submit')}
              </Button>
            </div>
          </form>
        )}
        <footer className='bg-white px-6 pb-6 pt-3 text-center text-sm text-slate-600'>{t('publicForm.poweredBy')}</footer>
      </article>
    </main>
  );
};
